import React, { useMemo, useState, useEffect } from 'react';
import { toast } from 'sonner';
import { coworkForestaApi } from '../../../lib/api';
import { formatDate } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { TabsContent } from '../../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../components/ui/tooltip';
import { Switch } from '../../../components/ui/switch';
import { Download, Edit2, Eye, FileUp, Home, Loader2, Plus, Search, Trash2, X } from 'lucide-react';
const FORESTA_ID = 'edif_6034e848d0ed';
const COWORK_AGG_ID = 'cowork-foresta-aggregated';
const COWORK_PROP_LABEL = 'Global Citizen';

// Conjuntos persistidos en DB (campo `conjunto_alquilable_id`) para Torre Emilia.
const CONJUNTO_PISO1 = 'conj_piso1_te';
const CONJUNTO_SUBSUELO = 'conj_subsuelo_te';

const BLOQUE_DEFS = {
  [CONJUNTO_PISO1]: {
    id: CONJUNTO_PISO1,
    nombre: 'Piso 1',
    descripcion: 'Bloque operativo: oficinas O101–O105 + 26 puestos cowork + cocheras C007/C008.',
    badge: 'Bloque',
  },
  [CONJUNTO_SUBSUELO]: {
    id: CONJUNTO_SUBSUELO,
    nombre: 'Cocheras Subsuelo',
    descripcion: 'Conjunto alquilable: 10 cocheras del subsuelo (C009–C018).',
    badge: 'Conjunto',
  },
};

const SERVICE_FILTERS = [
  { value: 'todas', label: 'Todas' },
  { value: 'busqueda', label: 'Con búsqueda' },
  { value: 'admin', label: 'Con administración' },
  { value: 'ambos', label: 'Ambos servicios' },
  { value: 'sin', label: 'Sin servicios' },
];

const propietarioResumen = (u) => {
  const lista = (u.propietarios || []).filter(p => p && p.propietario_nombre);
  if (lista.length > 0) {
    const first = lista[0].propietario_nombre;
    const extra = lista.length - 1;
    return extra > 0 ? `${first} + ${extra}` : first;
  }
  return u.propietario_nombre || '-';
};

const tieneBusqueda = (u) => !!u.servicio_busqueda_inquilino_activo && u.estado_alquiler !== 'alquilada';
const tieneAdmin = (u) => !!u.en_administracion;
const estaAlquilada = (u) => u.estado_alquiler === 'alquilada';

const buildCoworkAggregateRow = (coworkUnidades) => {
  // Cowork como bloque único en la tab Admin:
  //  - "Búsqueda inquilino" Sí si TODOS los puestos la tienen activa, parcial si algunos, No si ninguno.
  //  - "Administración" idem.
  //  - El contrato del bloque se canoniza en el PRIMER puesto del array (master).
  const total = coworkUnidades.length;
  const busqOn = coworkUnidades.filter(u => !!u.servicio_busqueda_inquilino_activo).length;
  const adminOn = coworkUnidades.filter(u => !!u.en_administracion).length;
  const busqAll = total > 0 && busqOn === total;
  const adminAll = total > 0 && adminOn === total;
  const master = coworkUnidades[0] || {};
  return {
    unidad_id: COWORK_AGG_ID,
    nombre_unidad: 'Cowork',
    tipo: 'cowork',
    propietario_nombre: COWORK_PROP_LABEL,
    propietarios: [],
    en_administracion: adminAll,
    tipo_administracion: master.tipo_administracion || 'simple',
    porcentaje_administracion: master.porcentaje_administracion ?? (master.tipo_administracion === 'full' ? 20 : 8),
    servicio_busqueda_inquilino_activo: busqAll,
    // El contrato del bloque vive en la unidad master (el primer puesto).
    contrato_busqueda_inquilino: master.contrato_busqueda_inquilino || null,
    contrato_administracion: master.contrato_administracion || null,
    __aggregated: true,
    __count: total,
    __busq_on: busqOn,
    __admin_on: adminOn,
    __children: coworkUnidades,
    __master_id: master.unidad_id || null,
    // Lista de unidad_ids actualmente en administración. Se usa para edición bulk del fee/tipo
    // de administración del Cowork (las unidades en admin reciben el mismo valor en una sola request).
    __bulk_ids: coworkUnidades.filter(u => !!u.en_administracion).map(u => u.unidad_id),
  };
};

// ---------- UI atoms ----------
const Badge = ({ tone = 'gray', children, testid }) => {
  const tones = {
    green: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100',
    blue: 'bg-blue-50 text-blue-700 ring-1 ring-blue-100',
    gray: 'bg-slate-100 text-slate-500 ring-1 ring-slate-200',
  };
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${tones[tone]}`}
      data-testid={testid}
    >
      {children}
    </span>
  );
};

const Tip = ({ label, children }) => (
  <TooltipProvider delayDuration={200}>
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="top">{label}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

// Celda de contrato (búsqueda / administración) estilo Torre Emilia para Foresta.
// - has=false → botón "Subir" (file input oculto, accept = pdf/doc/imagen).
// - has=true  → link de descarga con nombre truncado + botón "Reemplazar".
const ContratoCellForesta = ({ has, url, nombre, onUpload, testidUpload, testidDownload, testidReplace }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = React.useRef(null);
  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    Promise.resolve(onUpload?.(f)).finally(() => {
      setUploading(false);
      e.target.value = '';
    });
  };
  return (
    <div className="flex items-center justify-center gap-1.5">
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
        className="hidden"
        onChange={onPick}
      />
      {has ? (
        <>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            title={nombre || 'Descargar contrato'}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 max-w-[160px] truncate"
            data-testid={testidDownload}
          >
            <Download className="w-3 h-3 shrink-0" />
            <span className="truncate">{nombre || 'Descargar'}</span>
          </a>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 px-1.5 text-[11px] text-slate-500 hover:text-slate-900"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            title="Reemplazar"
            data-testid={testidReplace}
          >
            {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileUp className="w-3 h-3" />}
          </Button>
        </>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 text-[11px] gap-1"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid={testidUpload}
        >
          {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileUp className="w-3 h-3" />}
          {uploading ? 'Subiendo…' : 'Subir'}
        </Button>
      )}
    </div>
  );
};


// Línea secundaria "Doc: OK / Doc: Falta" + acciones de contrato (subir/ver/reemplazar)
const DocLine = ({ has, onUpload, viewUrl, testidUpload, testidView }) => {
  return (
    <div className="flex items-center gap-1 text-xs text-slate-500 mt-0.5">
      <span>Doc:</span>
      {has ? (
        <span className="font-medium text-emerald-700">OK</span>
      ) : (
        <span className="font-medium text-orange-600">Falta</span>
      )}
      {has && viewUrl && (
        <Tip label="Ver contrato">
          <a
            href={viewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-1 inline-flex items-center justify-center w-5 h-5 text-slate-400 hover:text-slate-700 rounded"
            data-testid={testidView}
          >
            <Eye className="w-3.5 h-3.5" />
          </a>
        </Tip>
      )}
      <Tip label={has ? 'Reemplazar contrato' : 'Subir contrato'}>
        <label
          className="inline-flex items-center justify-center w-5 h-5 text-slate-400 hover:text-slate-700 rounded cursor-pointer"
          data-testid={testidUpload}
        >
          <FileUp className="w-3.5 h-3.5" />
          <input
            type="file"
            accept=".pdf,.doc,.docx"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files && e.target.files[0];
              if (f) onUpload(f);
              e.target.value = '';
            }}
          />
        </label>
      </Tip>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// Modal detalle Cowork (Foresta) — gestión de la MODALIDAD de administración.
// Fuente de verdad: en_administracion. El estado de ocupación (alquilada/vacante/
// uso_propio) es DERIVADO (contratos) y NO editable acá.
// ════════════════════════════════════════════════════════════════════════════
const ESTADO_BADGE = {
  alquilada: { label: 'Alquilado', cls: 'bg-blue-50 text-blue-700 ring-blue-200' },
  vacante: { label: 'Vacante', cls: 'bg-amber-50 text-amber-700 ring-amber-200' },
  uso_propio: { label: 'Uso propietario', cls: 'bg-slate-100 text-slate-600 ring-slate-200' },
  error_integridad: { label: 'Error integridad', cls: 'bg-red-50 text-red-700 ring-red-200' },
};

const CoworkDetalleModal = ({ open, onOpenChange, edificioId }) => {
  const [loading, setLoading] = useState(false);
  const [resumen, setResumen] = useState(null);
  const [puestos, setPuestos] = useState([]);
  const [confirm, setConfirm] = useState(null); // { puesto, nuevo }
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [motivo, setMotivo] = useState('');
  const [saving, setSaving] = useState(false);

  const cargar = async () => {
    if (!edificioId) return;
    setLoading(true);
    try {
      const { data } = await coworkForestaApi.listPuestos(edificioId);
      setResumen(data.resumen);
      setPuestos(data.puestos);
    } catch {
      toast.error('Error cargando puestos del cowork');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) { cargar(); setConfirm(null); setMotivo(''); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, edificioId]);

  const pedirCambio = (puesto, nuevo) => {
    setFecha(new Date().toISOString().slice(0, 10));
    setMotivo('');
    setConfirm({ puesto, nuevo });
  };

  const aplicar = async () => {
    if (!confirm) return;
    if (!fecha) { toast.error('La fecha efectiva es obligatoria'); return; }
    setSaving(true);
    try {
      await coworkForestaApi.setAdministracion(edificioId, confirm.puesto.unidad_id, {
        en_administracion: confirm.nuevo,
        fecha_efectiva: fecha,
        motivo: motivo,
      });
      toast.success('Modalidad actualizada');
      setConfirm(null);
      await cargar();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d) {
        toast.error(`${d.mensaje} (Contrato ${d.contrato_numero || d.contrato_id || ''} · ${d.inquilino || 's/inquilino'})`);
      } else {
        toast.error('No se pudo actualizar la modalidad');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-5xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="cowork-detalle-dialog">
        <DialogHeader>
          <DialogTitle>Detalle Cowork — Modalidad de administración</DialogTitle>
          <p className="text-xs text-slate-500 mt-1">
            Elegí qué puestos administra Global Citizen (participan del pool). El estado
            de ocupación (Alquilado/Vacante) se deriva automáticamente de los contratos.
          </p>
        </DialogHeader>

        {resumen && (
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="px-2 py-1 rounded bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" data-testid="cowork-detalle-resumen-admin">Administrados: {resumen.administrados}/{resumen.total_puestos}</span>
            <span className="px-2 py-1 rounded bg-slate-100 text-slate-600 ring-1 ring-slate-200" data-testid="cowork-detalle-resumen-uso">Uso propietario: {resumen.uso_propietario}/{resumen.total_puestos}</span>
            <span className="px-2 py-1 rounded bg-blue-50 text-blue-700 ring-1 ring-blue-200">Alquilados: {resumen.alquilados}</span>
            <span className="px-2 py-1 rounded bg-amber-50 text-amber-700 ring-1 ring-amber-200">Vacantes: {resumen.vacantes}</span>
            {resumen.errores_integridad > 0 && (
              <span className="px-2 py-1 rounded bg-red-50 text-red-700 ring-1 ring-red-200">Errores integridad: {resumen.errores_integridad}</span>
            )}
          </div>
        )}

        <div className="flex-1 overflow-y-auto -mx-6 px-6 mt-2">
          {loading ? (
            <div className="flex items-center justify-center h-40 text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando…</div>
          ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 z-10">
              <tr className="border-b border-slate-200">
                <th className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 py-2 px-3">Puesto</th>
                <th className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 py-2 px-3">Propietario</th>
                <th className="text-center text-[11px] font-semibold uppercase tracking-wider text-slate-500 py-2 px-3">Administrado para renta</th>
                <th className="text-center text-[11px] font-semibold uppercase tracking-wider text-slate-500 py-2 px-3">Estado (auto)</th>
                <th className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 py-2 px-3">Inquilino</th>
                <th className="text-center text-[11px] font-semibold uppercase tracking-wider text-slate-500 py-2 px-3">Pool</th>
                <th className="text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500 py-2 px-3">Particip.</th>
              </tr>
            </thead>
            <tbody>
              {puestos.map((p) => {
                const b = ESTADO_BADGE[p.estado] || ESTADO_BADGE.vacante;
                return (
                  <tr key={p.unidad_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`cowork-detalle-row-${p.unidad_id}`}>
                    <td className="py-2 px-3 font-medium text-slate-900 whitespace-nowrap">{p.nombre_unidad}</td>
                    <td className="py-2 px-3 text-slate-700">{p.propietario_nombre || '—'}</td>
                    <td className="py-2 px-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Switch
                          checked={p.en_administracion}
                          onCheckedChange={(val) => pedirCambio(p, val)}
                          data-testid={`cowork-admin-toggle-${p.unidad_id}`}
                        />
                        <span className="text-xs text-slate-600">{p.en_administracion ? 'Sí' : 'No'}</span>
                      </div>
                    </td>
                    <td className="py-2 px-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ring-1 ${b.cls}`}>{b.label}</span>
                    </td>
                    <td className="py-2 px-3 text-slate-700">{p.inquilino || '—'}{p.contrato_numero ? <span className="block text-[11px] text-slate-400">Contr. {p.contrato_numero} · {formatDate(p.fecha_inicio)}→{formatDate(p.fecha_fin)}</span> : null}</td>
                    <td className="py-2 px-3 text-center">{p.participa_pool ? '✓' : '—'}</td>
                    <td className="py-2 px-3 text-right font-mono text-slate-700">{p.participacion}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          )}
        </div>

        {confirm && (
          <div className="border-t border-slate-200 pt-3 mt-1 bg-slate-50 -mx-6 px-6 pb-1" data-testid="cowork-confirm-box">
            <div className="text-sm font-medium text-slate-800 mb-2">
              {confirm.puesto.nombre_unidad}: {confirm.nuevo ? 'pasar a Administrado para renta' : 'pasar a Uso propietario (fuera del pool)'}
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="text-xs text-slate-500 block mb-1">Fecha efectiva *</label>
                <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className="w-40" data-testid="cowork-confirm-fecha" />
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs text-slate-500 block mb-1">Motivo / observación</label>
                <Input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Motivo del cambio" data-testid="cowork-confirm-motivo" />
              </div>
              <Button onClick={aplicar} disabled={saving} data-testid="cowork-confirm-aplicar">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmar cambio'}
              </Button>
              <Button variant="outline" onClick={() => setConfirm(null)} data-testid="cowork-confirm-cancelar">Cancelar</Button>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cowork-detalle-cerrar-editable">Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const TabAdministracion = ({
  cn,
  edificio,
  edificioId,
  handleOpenAltaAdmin,
  handleUploadContratoAdmin,
  handleDeleteContratoAdmin,
  handleUploadContratoBusqueda,
  setBajaAdminTarget,
  handleBajaServicioBusqueda,
  handleActivarServicioBusqueda,
  onBulkToggleAdmin,
  onEditAdmin,
  sortUnidades,
  unidades,
}) => {
  const esTorreEmilia = edificio?.modelo_administracion === 'gc_factura_y_cobra';

  // ── Hooks SIEMPRE se ejecutan (rules-of-hooks). Foresta-only state pero
  //    si el modelo no es Foresta, simplemente queda sin usar. ──
  const [filtroServicio, setFiltroServicio] = useState('todas');
  const [searchTerm, setSearchTerm] = useState('');
  const [coworkDetalleOpen, setCoworkDetalleOpen] = useState(false);

  // Foresta: agrupar cowork en una sola fila virtual
  const unidadesVista = useMemo(() => {
    if (edificioId !== FORESTA_ID) return unidades;
    const cowork = unidades.filter(u => u.tipo === 'cowork');
    if (cowork.length === 0) return unidades;
    const otras = unidades.filter(u => u.tipo !== 'cowork');
    return [...otras, buildCoworkAggregateRow(cowork)];
  }, [unidades, edificioId]);

  const coworkChildren = useMemo(
    () => unidadesVista.find(u => u.unidad_id === COWORK_AGG_ID)?.__children || [],
    [unidadesVista]
  );

  if (esTorreEmilia) {
    return (
      <TabAdministracionTorreEmilia
        unidades={unidades}
        onBulkToggleAdmin={onBulkToggleAdmin}
        handleUploadContratoAdmin={handleUploadContratoAdmin}
        handleDeleteContratoAdmin={handleDeleteContratoAdmin}
      />
    );
  }

  // ── Foresta y otros modelos: render legacy (sin cambios) ──

  // Contadores (contrato faltante NO afecta el conteo del servicio activo)
  const totalBusqueda = unidadesVista.filter(tieneBusqueda).length;
  const totalAdmin = unidadesVista.filter(tieneAdmin).length;
  const totalAmbos = unidadesVista.filter(u => tieneBusqueda(u) && tieneAdmin(u)).length;
  const totalSin = unidadesVista.filter(u => !tieneBusqueda(u) && !tieneAdmin(u)).length;
  const totalTodas = unidadesVista.length;

  const unidadesFiltradas = unidadesVista.filter(u => {
    // Filtro por servicio
    let pasa = true;
    switch (filtroServicio) {
      case 'busqueda': pasa = tieneBusqueda(u); break;
      case 'admin': pasa = tieneAdmin(u); break;
      case 'ambos': pasa = tieneBusqueda(u) && tieneAdmin(u); break;
      case 'sin': pasa = !tieneBusqueda(u) && !tieneAdmin(u); break;
      default: pasa = true;
    }
    if (!pasa) return false;
    // Filtro por buscador: SOLO por el número/nombre ACTUAL de la oficina.
    // Se ignora el sufijo "(ex …)" y cualquier otro campo (propietario, tipo) para
    // evitar coincidencias irrelevantes (p. ej. buscar "309" y que aparezca "(ex 309)").
    const q = searchTerm.trim().toLowerCase();
    if (!q) return true;
    const nombreActual = String(u.nombre_unidad || '').split(/\s*\(ex/i)[0].trim().toLowerCase();
    return nombreActual.includes(q);
  });

  return (
    <TabsContent value="administracion" className="mt-0">
      <div className="space-y-3 flex-1 min-h-0 flex flex-col">
        {/* Toolbar: filtros + botón alta */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-slate-500">Filtrar:</span>
            {SERVICE_FILTERS.map(f => {
              const count =
                f.value === 'busqueda' ? totalBusqueda :
                f.value === 'admin' ? totalAdmin :
                f.value === 'ambos' ? totalAmbos :
                f.value === 'sin' ? totalSin :
                totalTodas;
              return (
                <Button
                  key={f.value}
                  variant={filtroServicio === f.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFiltroServicio(f.value)}
                  className="text-xs"
                  data-testid={`filter-${f.value}`}
                >
                  {f.label}
                  <span className="ml-1 text-xs opacity-70">({count})</span>
                </Button>
              );
            })}
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <Input
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por número de oficina..."
              className="pl-9 pr-8 h-9 text-sm"
              data-testid="foresta-admin-search"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                aria-label="Limpiar búsqueda"
                data-testid="foresta-admin-search-clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Tabla */}
        <div className="bg-white rounded-lg border border-slate-200 flex-1 min-h-0 flex flex-col overflow-hidden">
          {unidadesFiltradas.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <Home className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-lg font-medium text-slate-700">
                {searchTerm
                  ? 'No hay resultados para esa búsqueda'
                  : filtroServicio === 'sin'
                    ? 'Todas las unidades del filtro tienen al menos un servicio'
                    : 'No hay unidades con este filtro'}
              </p>
              <p className="text-sm mt-2">
                {searchTerm
                  ? 'Probá con otros términos o limpiá el buscador.'
                  : 'Activá los servicios desde el switch de cada fila.'}
              </p>
            </div>
          ) : (
            <div className="flex-1 min-h-0 overflow-auto">
              <table className="w-full min-h-full">
                <thead className="sticky top-0 z-20 bg-slate-50 shadow-[0_1px_0_0_rgb(226,232,240)]">
                  <tr className="border-b border-slate-200">
                    <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4 whitespace-nowrap w-[170px] bg-slate-50">Unidad</th>
                    <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-2 whitespace-nowrap w-[90px] bg-slate-50">Tipo</th>
                    <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-3 whitespace-nowrap bg-slate-50">Propietario</th>
                    <th className="text-center text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-2 whitespace-nowrap w-[110px] bg-slate-50">Búsqueda</th>
                    <th className="text-center text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-2 whitespace-nowrap w-[120px] bg-slate-50">Contrato</th>
                    <th className="text-center text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-2 whitespace-nowrap w-[170px] bg-slate-50">Administración</th>
                    <th className="text-center text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-2 whitespace-nowrap w-[120px] bg-slate-50">Contrato</th>
                  </tr>
                </thead>
                <tbody>
                  {sortUnidades(unidadesFiltradas).map(unidad => {
                    const hasBusq = tieneBusqueda(unidad);
                    const hasAdmin = tieneAdmin(unidad);
                    const isAgg = !!unidad.__aggregated;
                    const alquilada = estaAlquilada(unidad);
                    const hasContratoBusq = !!(unidad.contrato_busqueda_inquilino && (unidad.contrato_busqueda_inquilino.url || unidad.contrato_busqueda_inquilino.nombre));
                    const hasContratoAdmin = !!(unidad.contrato_administracion && (unidad.contrato_administracion.url || unidad.contrato_administracion.nombre));

                    return (
                      <tr key={unidad.unidad_id} className="border-b border-slate-100 hover:bg-slate-50 align-middle" data-testid={`servicios-row-${unidad.unidad_id}`}>
                        {/* Unidad */}
                        <td className="py-3 px-4">
                          <p className="font-semibold text-slate-900 text-sm whitespace-nowrap" title={unidad.nombre_unidad}>{unidad.nombre_unidad}</p>
                          {isAgg && <p className="text-[11px] text-slate-500 mt-0.5 whitespace-nowrap">{unidad.__count} unidades · vista agrupada</p>}
                        </td>

                        {/* Tipo */}
                        <td className="py-3 px-3">
                          <span className="px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-700 capitalize">{unidad.tipo}</span>
                        </td>

                        {/* Propietario */}
                        <td className="py-3 px-3 text-sm text-slate-700 max-w-[200px] truncate" title={propietarioResumen(unidad)}>
                          {propietarioResumen(unidad)}
                        </td>

                        {/* Búsqueda (Switch + badge Sí/No) — centrado */}
                        <td className="py-3 px-2 text-center" data-testid={`col-busqueda-${unidad.unidad_id}`}>
                          {alquilada ? (
                            <Badge tone="blue">Alquilada</Badge>
                          ) : (
                            <div className="flex items-center justify-center gap-1.5">
                              <Switch
                                checked={hasBusq}
                                onCheckedChange={async (val) => {
                                  if (isAgg) {
                                    // Aplicar a los 54 puestos cowork en bulk (uno por uno via handlers existentes).
                                    const ids = (unidad.__children || []).map(c => c.unidad_id);
                                    const fn = val ? handleActivarServicioBusqueda : handleBajaServicioBusqueda;
                                    if (!fn) return;
                                    await Promise.all(ids.map(id => Promise.resolve(fn(id)).catch(() => null)));
                                  } else if (val) {
                                    handleActivarServicioBusqueda && handleActivarServicioBusqueda(unidad.unidad_id);
                                  } else {
                                    handleBajaServicioBusqueda && handleBajaServicioBusqueda(unidad.unidad_id);
                                  }
                                }}
                                data-testid={`fst-busq-toggle-${unidad.unidad_id}`}
                                aria-label={`Toggle búsqueda ${unidad.nombre_unidad}`}
                              />
                              <span
                                className={
                                  hasBusq
                                    ? 'px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100'
                                    : isAgg && unidad.__busq_on > 0
                                      ? 'px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 ring-1 ring-amber-200'
                                      : 'px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500 ring-1 ring-slate-200'
                                }
                                data-testid={`busq-state-${unidad.unidad_id}`}
                              >
                                {hasBusq
                                  ? 'Sí'
                                  : isAgg && unidad.__busq_on > 0
                                    ? `Parcial · ${unidad.__busq_on}/${unidad.__count}`
                                    : 'No'}
                              </span>
                            </div>
                          )}
                        </td>

                        {/* Contrato búsqueda */}
                        <td className="py-3 px-2 text-center" data-testid={`col-contrato-busqueda-${unidad.unidad_id}`}>
                          {!hasBusq ? (
                            <span className="text-slate-300 text-sm" data-testid={`contrato-busqueda-na-${unidad.unidad_id}`}>—</span>
                          ) : (
                            <ContratoCellForesta
                              has={hasContratoBusq}
                              url={unidad.contrato_busqueda_inquilino?.url}
                              nombre={unidad.contrato_busqueda_inquilino?.nombre}
                              onUpload={(f) => handleUploadContratoBusqueda(isAgg ? unidad.__master_id : unidad.unidad_id, f)}
                              testidUpload={`contrato-busqueda-upload-${unidad.unidad_id}`}
                              testidDownload={`contrato-busqueda-download-${unidad.unidad_id}`}
                              testidReplace={`contrato-busqueda-replace-${unidad.unidad_id}`}
                            />
                          )}
                        </td>

                        {/* Administración (Switch + badge Sí/No + %) — centrado */}
                        <td className="py-3 px-2 text-center" data-testid={`col-admin-${unidad.unidad_id}`}>
                          <div className="flex items-center justify-center gap-1.5 flex-wrap">
                            {!isAgg && (
                            <Switch
                              checked={hasAdmin}
                              onCheckedChange={async (val) => {
                                if (isAgg) {
                                  const ids = (unidad.__children || []).map(c => c.unidad_id);
                                  if (val) {
                                    // Bulk ON via bulkToggleAdmin (no requiere alta dialog para cowork).
                                    onBulkToggleAdmin && (await onBulkToggleAdmin(ids, true));
                                  } else {
                                    onBulkToggleAdmin && (await onBulkToggleAdmin(ids, false));
                                  }
                                } else if (val) {
                                  handleOpenAltaAdmin({ unidad_id: unidad.unidad_id, servicio_admin_activo: true });
                                } else {
                                  setBajaAdminTarget && setBajaAdminTarget(unidad.unidad_id);
                                }
                              }}
                              data-testid={`fst-admin-toggle-${unidad.unidad_id}`}
                              aria-label={`Toggle administración ${unidad.nombre_unidad}`}
                            />
                            )}
                            <span
                              className={
                                isAgg
                                  ? 'px-2 py-0.5 rounded-full text-xs font-medium bg-slate-50 text-slate-700 ring-1 ring-slate-200'
                                  : hasAdmin
                                  ? 'px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100'
                                  : 'px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500 ring-1 ring-slate-200'
                              }
                              data-testid={`admin-state-${unidad.unidad_id}`}
                            >
                              {isAgg
                                ? `Administrados ${unidad.__admin_on}/${unidad.__count} · Uso propietario ${unidad.__count - unidad.__admin_on}/${unidad.__count}`
                                : hasAdmin ? 'Sí' : 'No'}
                            </span>
                            {hasAdmin && (() => {
                              const pct = unidad.porcentaje_administracion ?? (unidad.tipo_administracion === 'full' ? 20 : 8);
                              const tipoLabel = unidad.tipo_administracion === 'simple' ? 'Simple' : 'Full';
                              return (
                                <Tip label={tipoLabel}>
                                  <span className="px-1.5 py-0.5 rounded text-[11px] font-medium bg-slate-50 text-slate-700 ring-1 ring-slate-200">
                                    {pct}%
                                  </span>
                                </Tip>
                              );
                            })()}
                            {hasAdmin && !isAgg && (
                              <Tip label="Editar administración">
                                <button
                                  type="button"
                                  onClick={() => onEditAdmin && onEditAdmin(unidad)}
                                  className="inline-flex items-center justify-center w-6 h-6 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded"
                                  data-testid={`edit-admin-${unidad.unidad_id}`}
                                >
                                  <Edit2 className="w-3.5 h-3.5" />
                                </button>
                              </Tip>
                            )}
                            {isAgg && Array.isArray(unidad.__bulk_ids) && unidad.__bulk_ids.length > 0 && (
                              <Tip label={`Editar administración (${unidad.__bulk_ids.length} unidades)`}>
                                <button
                                  type="button"
                                  onClick={() => onEditAdmin && onEditAdmin(unidad)}
                                  className="inline-flex items-center justify-center w-6 h-6 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded"
                                  data-testid={`edit-admin-${unidad.unidad_id}`}
                                >
                                  <Edit2 className="w-3.5 h-3.5" />
                                </button>
                              </Tip>
                            )}
                            {isAgg && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-xs h-7 ml-1"
                                onClick={() => setCoworkDetalleOpen(true)}
                                data-testid="cowork-ver-detalle-btn"
                              >
                                <Eye className="w-3 h-3 mr-1" />
                                Ver detalle
                              </Button>
                            )}
                          </div>
                        </td>

                        {/* Contrato administración */}
                        <td className="py-3 px-2 text-center" data-testid={`col-contrato-admin-${unidad.unidad_id}`}>
                          {!hasAdmin ? (
                            <span className="text-slate-300 text-sm" data-testid={`contrato-admin-na-${unidad.unidad_id}`}>—</span>
                          ) : (
                            <ContratoCellForesta
                              has={hasContratoAdmin}
                              url={unidad.contrato_administracion?.url}
                              nombre={unidad.contrato_administracion?.nombre}
                              onUpload={(f) => handleUploadContratoAdmin(isAgg ? unidad.__master_id : unidad.unidad_id, f)}
                              testidUpload={`contrato-admin-upload-${unidad.unidad_id}`}
                              testidDownload={`contrato-admin-download-${unidad.unidad_id}`}
                              testidReplace={`contrato-admin-replace-${unidad.unidad_id}`}
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal Cowork detalle (editable — modalidad de administración) */}
      <CoworkDetalleModal open={coworkDetalleOpen} onOpenChange={setCoworkDetalleOpen} edificioId={edificioId} />
      {false && (
      <Dialog open={coworkDetalleOpen} onOpenChange={setCoworkDetalleOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="cowork-detalle-dialog">
          <DialogHeader>
            <DialogTitle>Detalle Coworking ({coworkChildren.length} unidades)</DialogTitle>
            <p className="text-xs text-slate-500 mt-1">
              Vista solo lectura. Las acciones sobre cada unidad cowork se gestionan desde la pestaña "1. Unidades".
            </p>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto -mx-6 px-6">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50 z-10">
                <tr className="border-b border-slate-200">
                  <th className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 py-2 px-3">Unidad</th>
                  <th className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 py-2 px-3">Tipo</th>
                  <th className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 py-2 px-3">Propietario</th>
                  <th className="text-center text-[11px] font-semibold uppercase tracking-wider text-slate-500 py-2 px-3">Administración</th>
                  <th className="text-center text-[11px] font-semibold uppercase tracking-wider text-slate-500 py-2 px-3">Búsqueda inquilino</th>
                  <th className="text-center text-[11px] font-semibold uppercase tracking-wider text-slate-500 py-2 px-3">Fee admin.</th>
                </tr>
              </thead>
              <tbody>
                {coworkChildren.map(u => {
                  const adminActivo = !!u.en_administracion;
                  const busqActivo = !!u.servicio_busqueda_inquilino_activo;
                  const fee = adminActivo
                    ? `${u.porcentaje_administracion ?? (u.tipo_administracion === 'full' ? 20 : 8)}%`
                    : '-';
                  return (
                    <tr
                      key={u.unidad_id}
                      className="border-b border-slate-100 hover:bg-slate-50"
                      data-testid={`cowork-detalle-row-${u.unidad_id}`}
                    >
                      <td className="py-2 px-3 font-medium text-slate-900 whitespace-nowrap">{u.nombre_unidad}</td>
                      <td className="py-2 px-3">
                        <span className="px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-700 capitalize">{u.tipo}</span>
                      </td>
                      <td className="py-2 px-3 text-slate-700">{propietarioResumen(u)}</td>
                      <td className="py-2 px-3 text-center">
                        {adminActivo ? (
                          <span className={cn(
                            'px-2 py-0.5 text-xs rounded-full font-medium',
                            u.tipo_administracion === 'simple' ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'
                          )}>
                            Sí · {u.tipo_administracion === 'simple' ? 'Simple' : 'Full'}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">No</span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-center">
                        {busqActivo ? (
                          <span className="px-2 py-0.5 text-xs rounded-full font-medium bg-emerald-50 text-emerald-700">
                            Activa
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">No</span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-center text-slate-700">{fee}</td>
                    </tr>
                  );
                })}
                {coworkChildren.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-slate-500 text-sm">
                      No hay unidades cowork cargadas.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCoworkDetalleOpen(false)}
              data-testid="cowork-detalle-cerrar"
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      )}
    </TabsContent>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// Sub-vista para Torre Emilia (modelo_administracion = "gc_factura_y_cobra").
// Tabla plana sin grupos legacy. Solo importa si Global Citizen administra
// o no. Sin servicios de búsqueda inquilino, admin mensual 8%, simple/full.
// ════════════════════════════════════════════════════════════════════════════
const propietarioResumenTE = (u) => {
  const lista = (u.propietarios || []).filter(p => p && p.propietario_nombre);
  if (lista.length === 0) return u.propietario_nombre || '—';
  const first = lista[0].propietario_nombre;
  return lista.length > 1 ? `${first} + ${lista.length - 1}` : first;
};
const TabAdministracionTorreEmilia = ({ unidades, onBulkToggleAdmin, handleUploadContratoAdmin, handleDeleteContratoAdmin }) => {
  const [detalleBloque, setDetalleBloque] = useState(null); // { id, nombre, miembros: [u,...] }
  const [busy, setBusy] = useState({}); // por rowKey

  // ── Particionamos las unidades en 4 secciones ──
  const vista = useMemo(() => {
    // Excluir cocheras componentes (heredan admin de su principal, no aparecen como filas).
    const noComponentes = unidades.filter(u => u.rol_unidad !== 'componente');

    // Excluir las unidades sintéticas "padre" de los bloques (ej. Piso 1 unit_piso1_te) —
    // representan el bloque pero NO son miembros del bloque. Se identifican por estar
    // en el bloque (conjunto_alquilable_id) y NO tener `unidad_principal_id` apuntando
    // a otra unidad (es decir, son raíz).
    const esUnidadPadreDeBloque = (u) =>
      u.conjunto_alquilable_id && !u.unidad_principal_id && (
        u.unidad_id === 'unit_piso1_te' || u.unidad_id === 'unit_subsuelo_te'
      );

    // Bloque Piso 1 (conj_piso1_te) — solo miembros, sin la unidad padre.
    const piso1 = noComponentes.filter(u =>
      u.conjunto_alquilable_id === CONJUNTO_PISO1 && !esUnidadPadreDeBloque(u)
    );
    // Bloque Subsuelo (conj_subsuelo_te) — solo miembros.
    const subsuelo = noComponentes.filter(u =>
      u.conjunto_alquilable_id === CONJUNTO_SUBSUELO && !esUnidadPadreDeBloque(u)
    );
    // El resto: unidades sueltas / principales visibles (también excluye los padres de bloque).
    const enBloque = new Set([...piso1, ...subsuelo].map(u => u.unidad_id));
    const sueltas = noComponentes.filter(u =>
      !enBloque.has(u.unidad_id) && !esUnidadPadreDeBloque(u)
    );

    // Dentro de sueltas separamos principales (oficinas/locales) y cocheras independientes
    const principales = sueltas.filter(u => u.tipo === 'oficina' || u.tipo === 'local');
    const cocherasSueltas = sueltas.filter(u => u.tipo === 'cochera');

    return { principales, piso1, subsuelo, cocherasSueltas };
  }, [unidades]);

  // Lookup: cocheras componentes asociadas a una principal
  const cocherasDePrincipal = useMemo(() => {
    const map = {};
    for (const u of unidades) {
      if (u.rol_unidad === 'componente' && u.unidad_principal_id) {
        (map[u.unidad_principal_id] ||= []).push(u);
      }
    }
    return map;
  }, [unidades]);

  // ── Estado de "administrada" del bloque: 'si' | 'no' | 'parcial' ──
  const estadoBloque = (miembros) => {
    if (miembros.length === 0) return 'no';
    const adminCount = miembros.filter(u => !!u.en_administracion).length;
    if (adminCount === 0) return 'no';
    if (adminCount === miembros.length) return 'si';
    return 'parcial';
  };

  const estadoAlquilerBloque = (miembros) => {
    const alquiladas = miembros.filter(u => u.estado_alquiler === 'alquilada').length;
    const reservadas = miembros.filter(u => u.estado_alquiler === 'reservada').length;
    return { alquiladas, reservadas, total: miembros.length };
  };

  // ── Toggle ──
  const togglearIds = async (rowKey, unidad_ids, desiredOn) => {
    if (!onBulkToggleAdmin) return;
    setBusy(b => ({ ...b, [rowKey]: true }));
    try {
      await onBulkToggleAdmin(unidad_ids, desiredOn);
    } finally {
      setBusy(b => ({ ...b, [rowKey]: false }));
    }
  };

  const renderToggleCell = (rowKey, miembros, label) => {
    const estado = estadoBloque(miembros);
    const on = estado === 'si';
    const ids = miembros.map(u => u.unidad_id);
    const handleChange = () => {
      // si está parcial o no → forzar todo a true; si está sí → forzar todo a false
      togglearIds(rowKey, ids, !on);
    };
    return (
      <div className="flex items-center justify-center gap-2">
        <Switch
          checked={on}
          onCheckedChange={handleChange}
          disabled={!!busy[rowKey]}
          data-testid={`te-admin-toggle-${rowKey}`}
          aria-label={`Toggle administración ${label}`}
        />
        {estado === 'si' && (
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100" data-testid={`te-admin-badge-${rowKey}`}>Sí</span>
        )}
        {estado === 'no' && (
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500 ring-1 ring-slate-200" data-testid={`te-admin-badge-${rowKey}`}>No</span>
        )}
        {estado === 'parcial' && (
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 ring-1 ring-amber-200" data-testid={`te-admin-badge-${rowKey}`}>
            Parcial · {miembros.filter(u => u.en_administracion).length}/{miembros.length}
          </span>
        )}
      </div>
    );
  };

  const renderEstadoAlquilerBloque = (miembros) => {
    const { alquiladas, reservadas, total } = estadoAlquilerBloque(miembros);
    return (
      <div className="text-xs text-slate-600">
        <span className="font-medium text-slate-900">{alquiladas}</span>
        <span className="text-slate-500"> de {total} alquiladas</span>
        {reservadas > 0 && <span className="text-amber-600"> · {reservadas} reservadas</span>}
      </div>
    );
  };

  const renderEstadoAlquilerIndividual = (u) => {
    // Si la unidad NO está en administración, no medimos ocupación → mostrar "—".
    if (!u.en_administracion) {
      return (
        <span className="inline-flex px-2 py-0.5 text-slate-400 text-xs" title="Unidad no administrada — la gestiona el propietario" data-testid={`te-estado-alq-${u.unidad_id}`}>
          —
        </span>
      );
    }
    const e = u.estado_alquiler || 'vacante';
    const tone =
      e === 'alquilada' ? 'bg-blue-50 text-blue-700 ring-blue-100' :
      e === 'reservada' ? 'bg-amber-50 text-amber-700 ring-amber-100' :
      'bg-slate-100 text-slate-500 ring-slate-200';
    const label = e === 'alquilada' ? 'Alquilada' : e === 'reservada' ? 'Reservada' : 'Vacante';
    return (
      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ring-1 ${tone}`} data-testid={`te-estado-alq-${u.unidad_id}`}>
        {label}
      </span>
    );
  };

  // ── Helpers de orden coherente con Tab "1. Unidades" ──
  // Orden canónico (igual que 5.Factura): oficinas por número (201..402) primero,
  // y al final Piso 5, 6, 7. (El bloque Piso 1 se renderiza aparte, arriba.)
  const tipoPrio = (t) => ({ oficina: 0, cowork: 1, local: 2, cochera: 3 })[String(t || '').toLowerCase()] ?? 4;
  const ordenUnidad = (nombre, tipo) => {
    const tp = tipoPrio(tipo);
    const n = String(nombre || '').trim();
    if (/^piso\s*1\b/i.test(n)) return [tp, 0, 0];
    const soloNum = n.match(/^(\d+)/);
    if (soloNum) return [tp, 1, parseInt(soloNum[1], 10)];
    const mPiso = n.match(/piso\s*(\d+)/i);
    if (mPiso) return [tp, 2, parseInt(mPiso[1], 10)];
    return [tp, 3, 9999];
  };
  const cmpOrden = (ka, kb) => {
    for (let i = 0; i < ka.length; i++) { if (ka[i] !== kb[i]) return ka[i] - kb[i]; }
    return 0;
  };
  const sortByTipoYNum = (arr) => [...arr].sort((a, b) =>
    cmpOrden(ordenUnidad(a.nombre_unidad, a.tipo), ordenUnidad(b.nombre_unidad, b.tipo))
  );

  // ── Totales arriba ──
  const totalFilas = vista.principales.length
    + (vista.piso1.length > 0 ? 1 : 0)
    + (vista.subsuelo.length > 0 ? 1 : 0)
    + vista.cocherasSueltas.length;

  const totalAdministradas =
    vista.principales.filter(u => u.en_administracion).length
    + (estadoBloque(vista.piso1) === 'si' ? 1 : 0)
    + (estadoBloque(vista.subsuelo) === 'si' ? 1 : 0)
    + vista.cocherasSueltas.filter(u => u.en_administracion).length;

  // ── Filas a renderizar (orden pedido por el usuario):
  //    1) Bloque Piso 1
  //    2) Oficinas / locales principales (orden tipo + número, igual que Tab 1)
  //    3) Cocheras sueltas independientes (C005, C006, C019)
  //    4) Conjunto Cocheras Subsuelo
  const filas = [];
  if (vista.piso1.length > 0) {
    filas.push({ kind: 'bloque', key: CONJUNTO_PISO1, def: BLOQUE_DEFS[CONJUNTO_PISO1], miembros: vista.piso1 });
  }
  sortByTipoYNum(vista.principales).forEach(u => {
    filas.push({ kind: 'principal', key: u.unidad_id, u });
  });
  sortByTipoYNum(vista.cocherasSueltas).forEach(u => {
    filas.push({ kind: 'principal', key: u.unidad_id, u });
  });
  if (vista.subsuelo.length > 0) {
    filas.push({ kind: 'bloque', key: CONJUNTO_SUBSUELO, def: BLOQUE_DEFS[CONJUNTO_SUBSUELO], miembros: vista.subsuelo });
  }

  // Renderiza la celda "Contrato adm." (Global Citizen ↔ Propietario).
  // - No administrada → "—"
  // - Administrada sin contrato → botón "Subir" (file input oculto)
  // - Administrada con contrato → link descarga + botón "Reemplazar"
  const [uploadingContratoAdm, setUploadingContratoAdm] = useState(null); // unidad_id en curso
  const renderContratoAdmCell = (u) => {
    if (!u.en_administracion) {
      return <span className="text-slate-300 text-sm">—</span>;
    }
    const archivo = u.contrato_administracion_archivo; // {filename, content_type, size, uploaded_at}
    const tieneArchivo = !!(archivo && archivo.filename);
    const isUploading = uploadingContratoAdm === u.unidad_id;
    const inputId = `contrato-adm-input-${u.unidad_id}`;
    const downloadUrl = tieneArchivo
      ? `${process.env.REACT_APP_BACKEND_URL}/api/unidades/${u.unidad_id}/contrato-administracion/download`
      : null;
    const onPickFile = (e) => {
      const f = e.target.files?.[0];
      if (!f) return;
      setUploadingContratoAdm(u.unidad_id);
      Promise.resolve(handleUploadContratoAdmin?.(u.unidad_id, f))
        .finally(() => {
          setUploadingContratoAdm(null);
          e.target.value = '';
        });
    };
    return (
      <div className="flex items-center justify-center gap-1.5" data-testid={`contrato-adm-cell-${u.unidad_id}`}>
        <input
          id={inputId}
          type="file"
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          className="hidden"
          onChange={onPickFile}
          data-testid={`contrato-adm-input-${u.unidad_id}`}
        />
        {tieneArchivo ? (
          <>
            <a
              href={downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              title={archivo.filename}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 max-w-[140px] truncate"
              data-testid={`contrato-adm-download-${u.unidad_id}`}
            >
              <Download className="w-3 h-3 shrink-0" />
              <span className="truncate">{archivo.filename}</span>
            </a>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 px-1.5 text-[11px] text-slate-500 hover:text-slate-900"
              onClick={() => document.getElementById(inputId)?.click()}
              disabled={isUploading}
              title="Reemplazar"
              data-testid={`contrato-adm-replace-${u.unidad_id}`}
            >
              {isUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileUp className="w-3 h-3" />}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 px-1.5 text-[11px] text-rose-500 hover:text-rose-700 hover:bg-rose-50"
              onClick={() => {
                if (window.confirm('¿Eliminar contrato de administración?')) {
                  handleDeleteContratoAdmin?.(u.unidad_id);
                }
              }}
              disabled={isUploading}
              title="Eliminar contrato"
              data-testid={`contrato-adm-delete-${u.unidad_id}`}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-[11px] gap-1"
            onClick={() => document.getElementById(inputId)?.click()}
            disabled={isUploading}
            data-testid={`contrato-adm-upload-${u.unidad_id}`}
          >
            {isUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileUp className="w-3 h-3" />}
            {isUploading ? 'Subiendo…' : 'Subir'}
          </Button>
        )}
      </div>
    );
  };

  const renderPrincipal = (u) => {
    const cocheras = cocherasDePrincipal[u.unidad_id] || [];
    const nombresCocheras = cocheras.map(c => c.nombre_unidad).sort().join(', ');
    return (
      <tr key={u.unidad_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`te-admin-row-${u.unidad_id}`}>
        <td className="py-2.5 px-3">
          <p className="text-sm font-semibold text-slate-900 whitespace-nowrap" title={u.nombre_unidad}>{u.nombre_unidad}</p>
        </td>
        <td className="py-2.5 px-3">
          <span className="px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-700 capitalize">{u.tipo}</span>
        </td>
        <td className="py-2.5 px-3 text-sm text-slate-700 max-w-[200px] truncate" title={propietarioResumenTE(u)}>
          {propietarioResumenTE(u)}
        </td>
        <td className="py-2.5 px-3 text-center">
          {cocheras.length > 0 ? (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700 cursor-help">
                    {cocheras.length}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top">{nombresCocheras}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <span className="text-slate-300">—</span>
          )}
        </td>
        <td className="py-2.5 px-3">
          {renderToggleCell(u.unidad_id, [u], u.nombre_unidad)}
        </td>
        <td className="py-2.5 px-3 text-center">
          {renderContratoAdmCell(u)}
        </td>
        <td className="py-2.5 px-3">
          {renderEstadoAlquilerIndividual(u)}
        </td>
      </tr>
    );
  };

  const renderBloque = (f) => {
    const { def, miembros } = f;
    const cocherasCount = miembros.filter(u => u.tipo === 'cochera').length;
    const nombresCocheras = miembros.filter(u => u.tipo === 'cochera').map(u => u.nombre_unidad).sort().join(', ');
    // Para el contrato adm. del bloque: usamos la unidad sintética "padre" del bloque
    // (Piso 1 unit_piso1_te) si está cargada en `unidades`. Permite subir/descargar igual
    // que cualquier unidad principal.
    const unidadPadre = unidades.find(x =>
      x.conjunto_alquilable_id === f.key && !x.unidad_principal_id
    );
    return (
      <tr key={f.key} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`te-admin-bloque-${f.key}`}>
        <td className="py-2.5 px-3">
          <p className="text-sm font-semibold text-slate-900 whitespace-nowrap">{def.nombre}</p>
        </td>
        <td className="py-2.5 px-3">
          <div className="inline-flex items-center gap-1.5">
            <span className="px-2 py-0.5 rounded-full text-xs bg-indigo-100 text-indigo-700">{def.badge}</span>
            <button
              type="button"
              onClick={() => setDetalleBloque({ id: f.key, nombre: def.nombre, miembros })}
              className="inline-flex items-center justify-center w-6 h-6 rounded text-slate-500 hover:text-indigo-700 hover:bg-indigo-50 transition-colors"
              title="Ver detalle del bloque"
              data-testid={`te-admin-ver-detalle-${f.key}`}
            >
              <Eye className="w-3.5 h-3.5" />
            </button>
          </div>
        </td>
        <td className="py-2.5 px-3 text-sm text-slate-500">—</td>
        <td className="py-2.5 px-3 text-center">
          {cocherasCount > 0 ? (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700 cursor-help">
                    {cocherasCount}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top">{nombresCocheras}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <span className="text-slate-300">—</span>
          )}
        </td>
        <td className="py-2.5 px-3">
          {renderToggleCell(f.key, miembros, def.nombre)}
        </td>
        <td className="py-2.5 px-3 text-center">
          {unidadPadre
            ? renderContratoAdmCell(unidadPadre)
            : <span className="text-slate-300 text-sm">—</span>}
        </td>
        <td className="py-2.5 px-3">
          {renderEstadoAlquilerBloque(miembros)}
        </td>
      </tr>
    );
  };

  return (
    <TabsContent value="administracion" className="mt-0">
      <div className="space-y-2 flex-1 min-h-0 flex flex-col">
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden flex-1 min-h-0 flex flex-col" data-testid="tabla-admin-te">
          <div className="flex-1 min-h-0 overflow-auto relative">
            <table className="w-full min-h-full">
              <thead className="sticky top-0 bg-slate-50 z-20 shadow-[0_1px_0_0_rgb(226,232,240)]">
                <tr>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-3 whitespace-nowrap">Unidad / Bloque</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-3 whitespace-nowrap">Tipo</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-3 whitespace-nowrap">Propietario</th>
                  <th className="text-center text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-3 whitespace-nowrap">Cocheras</th>
                  <th className="text-center text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-3 whitespace-nowrap">Administrada por GC</th>
                  <th className="text-center text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-3 whitespace-nowrap">Contrato adm.</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-3 whitespace-nowrap">Estado alquiler</th>
                </tr>
              </thead>
              <tbody>
                {filas.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-slate-500">
                      <Home className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p className="text-sm">No hay unidades administrables en este edificio.</p>
                    </td>
                  </tr>
                ) : (
                  filas.map(f => f.kind === 'bloque' ? renderBloque(f) : renderPrincipal(f.u))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal detalle del bloque */}
      <Dialog open={!!detalleBloque} onOpenChange={(open) => !open && setDetalleBloque(null)}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="te-bloque-detalle-dialog">
          <DialogHeader>
            <DialogTitle>Detalle — {detalleBloque?.nombre} ({detalleBloque?.miembros?.length || 0} unidades)</DialogTitle>
            <p className="text-xs text-slate-500 mt-1">
              Vista solo lectura. La administración del bloque se controla con el toggle de la fila padre.
            </p>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto -mx-6 px-6">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50 z-10">
                <tr className="border-b border-slate-200">
                  <th className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 py-2 px-3">Unidad</th>
                  <th className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 py-2 px-3">Tipo</th>
                  <th className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 py-2 px-3">Propietario</th>
                  <th className="text-center text-[11px] font-semibold uppercase tracking-wider text-slate-500 py-2 px-3">Admin.</th>
                  <th className="text-center text-[11px] font-semibold uppercase tracking-wider text-slate-500 py-2 px-3">Estado alquiler</th>
                </tr>
              </thead>
              <tbody>
                {(detalleBloque?.miembros || []).map(u => (
                  <tr key={u.unidad_id} className="border-b border-slate-100" data-testid={`te-bloque-detalle-row-${u.unidad_id}`}>
                    <td className="py-2 px-3 font-medium text-slate-900 whitespace-nowrap">{u.nombre_unidad}</td>
                    <td className="py-2 px-3">
                      <span className="px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-700 capitalize">{u.tipo}</span>
                    </td>
                    <td className="py-2 px-3 text-slate-700">{propietarioResumenTE(u)}</td>
                    <td className="py-2 px-3 text-center">
                      {u.en_administracion ? (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">Sí</span>
                      ) : (
                        <span className="text-xs text-slate-400">No</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-center">{renderEstadoAlquilerIndividual(u)}</td>
                  </tr>
                ))}
                {(!detalleBloque?.miembros || detalleBloque.miembros.length === 0) && (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-slate-500 text-sm">
                      No hay unidades en este bloque.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDetalleBloque(null)}
              data-testid="te-bloque-detalle-cerrar"
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TabsContent>
  );
};

